import bankResource from "@/shared/resource/bank";
import { authorizedAxios } from "./authorizedAxios";
import {
  TpaIdResponse,
  PartnerInvestmentThreshold,
  DebitCardCustomization,
  PartnerCardCustomizationUpdateRequest,
  DebitCardCustomizationCreateRequest,
  DebitCardCustomizationUpdateRequest,
  BankCustodian,
  AvidiaPartner,
  AdminPendingTxSearchRequest,
  AdminPendingTxSearchResponse,
  InstantTransferPageResponseModel,
  InstantTransferFailureDetailModel,
  InstantTransferFailureRequest,
  ReimbursementFailurePageResponseModel,
  ReimbursementFailureDetailModel,
  ReimbursementFailurePageRequestModel,
  UmbAccountUser,
  UmbAccountUserList,
  UmbAccountUserModel,
  UmbReopenAccountRequest,
  AdminUmbLinkedAccountSearchRequest,
  AdminUmbLinkedAccountSearchResponse,
  PartnerAchBrandingRequest,
  PartnerAchBrandingResponse,
  HsaAccountDetails,
  InvestmentAccountRepointRequest,
  InvestmentRepointRequestResult
} from "@/admin/types";
import { EmployerAccounts,
  EmployerAccountsUpdate,
  BankAccountHoldsResponseModel,
  HoldTypeModel,
  BankAccountHoldUpdateRequest,
  BankAccountHoldsModel } from "@/employer/types";
import {
  TransactionEditSearch,
  TransactionEditPut,
  PartnerPortalStatus,
  PartnerPortalSingleSignOnConfig,
  TransactionResponse,
  TransactionFilter,
  BankTransactionDailyBalancePage,
  TransactionTotalsResponse,
  PendingContributions,
  PendingReimbursement,
  LinkedAccountApi,
  VerificationSubmission,
  LinkedAccountsGetResponse,
  ContactInformation,
  PartnerBankHsaUserValidationResponse,
  InvestmentBalancesResponse,
  BalanceResponse,
  PartnerBankPendingTransactionResponse,
  ReimburseBankRequest,
  BankProfile,
  UserPreferences,
  PersonalContributionRequest,
  TransactionSource,
  AchContributionState,
  BankDailyBalanceRecord,
  InvestmentDailyBalanceRecord,
  EmployerSuspension,
  InvestmentTransferRequest,
  InvestmentHistory,
  UserCustodianSettings,
  EmployerCustodianSettings,
  InvestmentAccountSsoResponse,
  InvestmentAccountCreateResponse,
  InvestmentProfile,
  BankAchAccountChoiceModel,
  BankAchValidationResponseModel,
  LinkedAccountDefaultUpdateRequest,
  BankAccountDefaultsRequest,
  BankAccountStatus,
  GetUserSystemIdResponse,
  InvestmentThreshold,
  PendingPersonalContribution,
  PendingPersonalContributionPut,
  TransactionEditSearchResult,
  DebitCardInfo,
  AvidiaStatementsList,
  PriorYearCutoffDateResponse,
  LinkedAccountManagementRequest,
  LinkedAccountManagementResponse,
  CurrentInvestmentBalances,
  InvestmentCustodian,
  CustomerFormDocumentResponse,
  FetchStateKey,
  HsabAccountDisplayTypeBatch,
  HsaAccountSummary,
  HsabInvestmentAccountStatus,
  HsabInvestmentIframe,
  HsabAccountTypesResponse,
  UserHasNotionalAccounts,
  NotionalBalanceResult,
  HsabAccountVerifyRequest,
  HsabAccountVerifyResponse,
  HsabAccountDisplayType,
  ReimburseBankResponse,
  HsaBankStatementsList,
  BankUserAccountNumber,
  HsaBankTaxDocumentList,
  AnonDocumentKey,
  HsabTransactionDetail,
  TransferSchedule,
  FutureContribution,
  ContributionType,
  GetAuthorizedSignerResponse,
  GetBeneficiaryResponse,
  AuthorizedSigner,
  AddAuthorizedSignerResponse,
  UpdateAuthorizedSignerResponse,
  AddBeneficiaryResponse,
  BeneficiaryUpdateRequest,
  BeneficiaryUpdateResponse,
  HsabEnrollmentSettingsUpdateRequest,
  DebitCardEligibilityResponse,
  AddAuthorizedSignerRequest,
  EmployerBankSettingsRequest,
  EmployerBankSettingsResponse,
  ContributionActivityList,
  InvestmentProvider, PlanYearEnrollmentDetailsResponse, HsabEnrollmentUpdateReimbursementMethodRequest
} from "@/shared/types";
import { AxiosResponse } from "axios";
import { errorIsStatus } from "@/shared/errorHelper";

export function getSavedTransactions (
    pageNumber: number,
    transactionFilter: TransactionFilter,
    username: string,
    partnerId: number,
    ofType?: TransactionSource,
    includePending?: boolean,
    pageSize?: number
  ) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<TransactionResponse>(
    bankResource.getSavedTransactions(pageNumber, transactionFilter, ofType, includePending, pageSize),
    query
  );
}

export function getHsabTransactions (
    username: string,
    partnerId: number,
    startDate: string,
    endDate: string
) {
  const userQuery = (username) ? { forUser: username, forUserPartnerId: partnerId } : {};
  const query = { startDate, endDate, ...userQuery};

  return authorizedAxios.get<HsabTransactionDetail[]>(
      bankResource.getHsabTransactions(),
      query
  );
}

export function getHsabTransactionExportFile (
  username: string,
  partnerId: number,
  startYear: number,
  endYear: number
) {
  const userQuery = (username) ? { forUser: username, forUserPartnerId: partnerId } : {};
  const query = { startYear, endYear, ...userQuery};

  return authorizedAxios.get(
    bankResource.getHsabTransactionExport(),
    query
  );
}

export function getHsabNotionalTransactionExportFile (accountId: number) {
  return authorizedAxios.get(bankResource.getHsabNotionalTransactionExport(accountId));
}

export function getInvestmentHistory (
  pageNumber: number,
  username: string,
  partnerId: number) {

  const query = (username) ? {
    page: pageNumber,
    forUser: username,
    forUserPartnerId: partnerId
  } : { page: pageNumber };

  return authorizedAxios.get<InvestmentHistory>(
    bankResource.getInvestmentHistory(),
    query
  );
}

export function getProfile (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<BankProfile>(bankResource.getProfile(), query);
}

export function getPreferences () {
    return authorizedAxios.get<UserPreferences>(bankResource.getPreferences());
}

export function putPreferences (userPreferences: UserPreferences) {
  return authorizedAxios.put<UserPreferences>(bankResource.getPreferences(), userPreferences);
}

export function getTransactionTotals (username: string, partnerId: number, forYear: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<TransactionTotalsResponse>(bankResource.getTransactionTotals(forYear), query);
}

export function getDailyBalances (startDate: string, endDate: string, username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<BankTransactionDailyBalancePage>(bankResource.getDailyBalances(startDate, endDate), data);
}

export function getInstantBalance (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<{balances: BalanceResponse}>(bankResource.instantBalance(), data);
}

export function getHsabInvestmentAccountStatus (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<HsabInvestmentAccountStatus>(bankResource.getHsabInvestmentAccountStatus(), data);
}

export function getHsabIframe (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.post<HsabInvestmentIframe>(bankResource.getHsabIframe(), data)
  .catch(err => {
    if (errorIsStatus(err, 404)) {
      const response = {... (err.response || {}), data: { url: null }};
      return response;
    }
    else {
      return Promise.reject(err);
    }
  });
}

export function getInvestmentBalances (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<CurrentInvestmentBalances>(bankResource.getInvestmentBalances(), data);
}

export function getInvestmentDailyBalances (
  username: string,
  partnerId: number,
  args: { startDate: string, endDate: string, investmentCustodian?: InvestmentCustodian }
) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<InvestmentBalancesResponse>(
    bankResource.getInvestmentDailyBalances(
      args.startDate,
      args.endDate,
      args.investmentCustodian
    ),
    data
  );
}

export function getInvestmentDailyBalancesList (
    username: string,
    partnerId: number,
    args: { startDate: string, endDate: string, investmentCustodian?: InvestmentCustodian }
) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<InvestmentBalancesResponse[]>(
      bankResource.getInvestmentDailyBalancesList(
          args.startDate,
          args.endDate,
          args.investmentCustodian
      ),
      data
  );
}

export function getPendingTransactions (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<PartnerBankPendingTransactionResponse>(bankResource.intradayTransactions(), query);
}

export function getPendingContributions (username: string, partnerId: number, contributionType?: ContributionType) {
  const query = username
      ? { forUser: username, forUserPartnerId: partnerId, contributionType }
      : { contributionType };
  return authorizedAxios.get<PendingContributions>(bankResource.getPendingContributions(), query);
}
export function getPendingReimbursements (username: string, partnerId: number) {
  const query = username ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<PendingReimbursement>(bankResource.pendingReimbursements(), query);
}
export function getTransactionsForDay (date: string) {
  return authorizedAxios.get<TransactionResponse>(bankResource.getTransactionsForDay(date));
}

export function employerSuspension () {
  return authorizedAxios.get<EmployerSuspension[]>(bankResource.employerSuspension());
}

export function getEmployerSuspensionByPartnerId (partnerId: number) {
  return authorizedAxios.get<EmployerSuspension[]>(bankResource.employerSuspensionByPartnerId(partnerId));
}

export function getEmployerSuspensionByEmployerId (partnerId: number, employerId: number) {
  return authorizedAxios.get<EmployerSuspension>(bankResource.employerSuspensionByEmployerId(partnerId, employerId));
}

export function updateEmployerSuspension (suspension: EmployerSuspension) {
  return authorizedAxios.post<EmployerSuspension[]>(bankResource.employerSuspension(), suspension);
}

export function putEmployerAccounts (employerId: number, data: EmployerAccountsUpdate) {
  return authorizedAxios.put<EmployerAccounts>(bankResource.employerAccounts(employerId), data);
}

export function getEmployerAccounts (employerId: number) {
  return authorizedAxios.get<EmployerAccounts>(bankResource.employerAccounts(employerId))
    .then(response => {
      if (response.data.feeAccount === null) {
        response.data.feeAccount = {
          bankAccountNumber: "",
          bankRoutingNumber: ""
        };
      }
      if (response.data.fundingAccount === null) {
        response.data.fundingAccount = {
          bankAccountNumber: "",
          bankRoutingNumber: ""
        };
      }
      return response;
    });
}

export function getHsaAccounts (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<HsaAccountSummary[]>(bankResource.accounts(), data);
}

// umb linked accounts for multi-account
export function umbAddLinkedAccount (linkedAccountApi: LinkedAccountApi)
  : Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.post<LinkedAccountApi>(
    bankResource.umbLinkedBankAccount(), linkedAccountApi);
}

export function umbDeleteLinkedAccount (linkedAccountApi: LinkedAccountApi)
  : Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.delete<LinkedAccountApi>(
    bankResource.umbLinkedBankAccount(linkedAccountApi.linkedBankAccountId));
}

export function umbUpdateLinkedAccount (linkedAccountApi: LinkedAccountApi)
  : Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.put<LinkedAccountApi>(bankResource.umbLinkedBankAccount(), linkedAccountApi);
}

export function umbGetLinkedAccount (param: number, param2: number): Promise<AxiosResponse<LinkedAccountsGetResponse>> {
  return authorizedAxios.get<LinkedAccountsGetResponse>(bankResource.umbLinkedAccountGet());
}

export function umbVerifyLinkedAccount (accountId: number, verificationSubmission: VerificationSubmission)
  : Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.post<LinkedAccountApi>(
    bankResource.umbLinkedAccountVerify(accountId), verificationSubmission);
}

export function umbReVerifyLinkedAccount (accountId: number): Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.post<LinkedAccountApi>(bankResource.umbLinkedAccountReVerify(accountId));
}

export function umbLinkedAccountsUpdateDefault (defaultsUpdateRequest: BankAccountDefaultsRequest)
  : Promise<AxiosResponse<LinkedAccountDefaultUpdateRequest>> {
  return authorizedAxios.post<LinkedAccountDefaultUpdateRequest>(
    bankResource.umbLinkedAccountsUpdateDefault(),
    defaultsUpdateRequest);
}

// linked accounts for multi-account
export function externalAccountAddLinkedAccount ()
  : Promise<AxiosResponse<BankAchValidationResponseModel>> {
  return authorizedAxios.post<BankAchValidationResponseModel>(
    bankResource.externalAccountLinkedBankAccountAdd());
}

export function externalAccountDeleteLinkedAccount (linkedAccountApi: LinkedAccountApi)
  : Promise<AxiosResponse<boolean>> {
  return authorizedAxios.delete<boolean>(
    bankResource.externalAccountLinkedBankAccountDelete(linkedAccountApi.linkedBankAccountId));
}

export function externalAccountAdminDeleteLinkedAccount (linkedAccountApi: LinkedAccountApi, username: string, partnerId: number)
  : Promise<AxiosResponse<boolean>> {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.delete<boolean>(
    bankResource.externalAccountLinkedBankAccountAdminDelete(partnerId, linkedAccountApi.linkedBankAccountId), data);
}

export function externalAccountLinkedBankAccountIsFraudAdmin (): Promise<AxiosResponse<boolean>> {
  return authorizedAxios.get<boolean>(bankResource.externalAccountLinkedBankAccountIsFraudAdmin());
}

export function externalAccountAdminReplaceLinkedAccount (linkedAccountApi: LinkedAccountApi, username: string, partnerId: number)
  : Promise<AxiosResponse<boolean>> {
  return authorizedAxios.post<boolean>(
    bankResource.externalAccountLinkedBankAccountAdminReplace(partnerId, linkedAccountApi.linkedBankAccountId, username), linkedAccountApi);
}

export function microEntryAccountDeleteAccount (accountId: number)
    : Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.delete<LinkedAccountApi>(
      bankResource.microEntryAccountDelete(accountId));
}

export function externalAccountUpdateLinkedAccount (linkedAccountApi: LinkedAccountApi)
  : Promise<AxiosResponse<LinkedAccountApi>> {
  return authorizedAxios.put<LinkedAccountApi>(
    bankResource.externalAccountLinkedBankAccountUpdate(), linkedAccountApi);
}

export function externalAccountGetLinkedAccount (username: string, partnerId: number)
  : Promise<AxiosResponse<LinkedAccountsGetResponse>> {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<LinkedAccountsGetResponse>(
    bankResource.externalAccountLinkedAccountGet(), data);
}
export function externalAccountReSyncLinkedAccount ()
  : Promise<AxiosResponse<LinkedAccountsGetResponse>> {
  return authorizedAxios.post<LinkedAccountsGetResponse>(
    bankResource.externalAccountLinkedAccountReSync());
}

export function externalAccountUpdateDefault (defaultsUpdateRequest: BankAccountDefaultsRequest)
    : Promise<AxiosResponse<LinkedAccountDefaultUpdateRequest>> {
  return authorizedAxios.post<LinkedAccountDefaultUpdateRequest>(
      bankResource.externalAccountLinkedAccountUpdateDefault(), defaultsUpdateRequest);
}

export function verifyHsaUser (contactInformation: ContactInformation) {
  return authorizedAxios.post<PartnerBankHsaUserValidationResponse>(bankResource.verifyHsaUser(), contactInformation);
}

export function setContactInformation (request: ContactInformation) {
  return authorizedAxios.put(bankResource.contactInformation(), request);
}

export function partnerPortalStatus () {
  return authorizedAxios.get<PartnerPortalStatus>(bankResource.partnerBankStatus());
}

export function partnerPortalSingleSignOnConfig (portal: string) {
  return authorizedAxios.get<PartnerPortalSingleSignOnConfig>(bankResource.partnerBankSingleSignOnConfig(portal));
}

export function reimburseRequest (request: ReimburseBankRequest) {
  return authorizedAxios.post<ReimburseBankResponse>(bankResource.reimburse(), request);
}

export function personalContributionRequest (request: PersonalContributionRequest) {
  return authorizedAxios.post<any>(bankResource.personalContribution(), request);
}

export function getPriorYearContributionEligibility () {
  return authorizedAxios.get<boolean>(bankResource.priorYearContributionEligibility());
}

export function getBankAccountStatus () {
  return authorizedAxios.get<BankAccountStatus>(bankResource.getBankAccountStatus());
}

export function getSavedContributions (pageNumber: number, username: string, partnerId: number,
                                       contributionType?: ContributionType) {
  const query = (username) ? {
      page: pageNumber,
      contributionType,
      forUser: username,
      forUserPartnerId: partnerId}
      : { page: pageNumber,
          contributionType};

  return authorizedAxios.get<TransactionResponse>(
    bankResource.getSavedContributions(), query);
}

export function getCurrentHsaBalance (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<BankDailyBalanceRecord>(bankResource.getCurrentHsaBalance(), query);
}

export function getTpaIds () {
  return authorizedAxios.get<TpaIdResponse>(bankResource.getTpaList());
}

export function getPartnerTpaId (partnerId: number) {
  return authorizedAxios.get<any>(bankResource.partnerTpaId(partnerId));
}

export function savePartnerTpaId (data: {tpaId: string, partnerId: number}) {
  return authorizedAxios.post<{tpaId: string, partnerId: number}>(bankResource.partnerTpaId(data.partnerId),
    {tpaId: data.tpaId, partnerId: data.partnerId });
}

export function getPartnerIndividualEmployerId (partnerId: number) {
  return authorizedAxios.get<any>(bankResource.partnerIndividualEmployerId(partnerId));
}

export function savePartnerIndividualEmployerId (data: {individualEmployerId: number, partnerId: number}) {
  return authorizedAxios
              .post<{individualEmployerId: number, partnerId: number}>(bankResource.partnerIndividualEmployerId(data.partnerId),
    {individualEmployerId: data.individualEmployerId, partnerId: data.partnerId });
}

export function validateEmployerPartnerChange (currentId: number, chosenId: number) {
  return authorizedAxios.get<any>(bankResource.validateEmployerPartner(currentId, chosenId));
}

export function investmentTransferRequest (request: InvestmentTransferRequest) {
  return authorizedAxios.post<InvestmentTransferRequest>(
    bankResource.requestInvestmentTransfer(),
    request);
}

export function getAchContributionTotalRecords (employerId: number) {
  return authorizedAxios.get<number>(bankResource.getAchContributionTotalRecords(employerId));
}

export function getInvestmentAccountSso () {
  return authorizedAxios.get<InvestmentAccountSsoResponse>(bankResource.getInvestmentAccountSso());
}

export function getInvestmentAccountCreateAccount () {
  return authorizedAxios.get<InvestmentAccountCreateResponse>(bankResource.getInvestmentAccountCreate());
}

export function saveInvestmentAccountNumber (accountNumber) {
  return authorizedAxios.put(bankResource.saveInvestmentAccountNumber(), {accountNumber});
}

export function saveInvestmentAccountError (error) {
  return authorizedAxios.put(bankResource.saveInvestmentAccountError(), {error});
}

export function getBalanceThresholds (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<InvestmentThreshold>(bankResource.getBalanceThresholds(), data);
}

export function getInvestmentProvider (username: string, partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<InvestmentProvider>(bankResource.getInvestmentProvider(), data);
}

export function getInvestmentProfile (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<InvestmentProfile>(bankResource.getInvestmentProfile(), query);
}

export function getAchContributions (employerId: number, page: number, pageSize: number) {
  return authorizedAxios.get<AchContributionState>(
    bankResource.getAchContributions(employerId, page, pageSize));
}

export function downloadEmployeeDisbursalReport (employerId: number, achReportId: number) {
  return authorizedAxios
    .get(bankResource.getEmployeeDisbursalReportDownload(employerId, achReportId));
}

export function getEmployerFundingDays (employerId: number) {
  return authorizedAxios.get(bankResource.employerSettings(employerId));
}
export function getEmployerBankSettings (employerId: number) {
  return authorizedAxios.get<EmployerBankSettingsResponse>(bankResource.employerBankSettings(employerId));
}
export function putEmployerBankSettings (request: EmployerBankSettingsRequest) {
  return authorizedAxios.put<EmployerBankSettingsResponse>(bankResource.employerBankSettings(request.employerId), request);
}
export function updateEmployerFundingDays (data: { employerId: number, days: number }) {
  return authorizedAxios.post<{ days: number }>(bankResource.employerSettings(data.employerId), { days: data.days});
}
export function getCustomerFormDocuments () {
  return authorizedAxios.get<CustomerFormDocumentResponse>(bankResource.getCustomerFormDocuments());
}
export function getEmployerTdaPlanId (employerId: number) {
  return authorizedAxios.get(bankResource.employerTdaPlanId(employerId));
}

export function updateEmployerTdaPlanId (data: { employerId: number, tdaPlanId: string }) {
  return authorizedAxios.post<string>
    (bankResource.employerTdaPlanId(data.employerId), {tdaPlanId: data.tdaPlanId});
}
export function getUserHsaCustodian (username: string, partnerId: number, loginPartnerId: number) {
  let data: object;
  if (loginPartnerId) {
    data = { loginPartnerId };
  } else {
    data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  }
  return authorizedAxios.get<UserCustodianSettings>(bankResource.getUserHsaCustodian(), data);
}

export function getEmployerCustodian (employerId: number) {
  return authorizedAxios.get<EmployerCustodianSettings>(bankResource.getEmployerCustodian(employerId));
}

export function getFinicityValidationUrl () {
  return authorizedAxios.request<string>(
    {
      method: "POST",
      baseURL: bankResource.postToGenerateValidationUrl()
    });
}

export function getFinicityMicroEntryAccountValidationUrl (accountId: string) {
  return authorizedAxios.request<string>(
      {
        method: "POST",
        baseURL: bankResource.microEntryAccountValidationUrl(accountId)
      });
}

export function getUserSystemId (partnerId: number, username: string) {
  return authorizedAxios.get<GetUserSystemIdResponse>(bankResource.getUserSystemId(partnerId), {username});
}
export function setUserSystemId (partnerId: number, cognitoUsername: string, userSystemId: string) {
  return authorizedAxios.post(bankResource.setUserSystemId(partnerId), {cognitoUsername, userSystemId});
}
export function getPartnerInvestmentThreshold (partnerId: number) {
  return authorizedAxios.get<PartnerInvestmentThreshold>(bankResource.getPartnerInvestmentThreshold(partnerId));
}
export function setPartnerInvestmentThreshold (partnerId: number, request: PartnerInvestmentThreshold) {
  return authorizedAxios.post(bankResource.setPartnerInvestmentThreshold(partnerId), request);
}
export function getEmployerInvestmentThreshold (employerId: number) {
  return authorizedAxios.get<InvestmentThreshold>(bankResource.getEmployerInvestmentThreshold(employerId));
}
export function setEmployerInvestmentThreshold (data: { employerId: number, investmentThreshold: InvestmentThreshold}) {
  return authorizedAxios.post(bankResource.setEmployerInvestmentThreshold(data.employerId), data.investmentThreshold);
}
export function listPendingContributions (pageNumber: number) {
  const query = { page: pageNumber, pageSize: 25 };
  return authorizedAxios.get<PendingPersonalContribution[]>(bankResource.listPendingContributions(), query);
}
export function getPendingContributionById (contributionId: number) {
  return authorizedAxios.get<PendingPersonalContribution>(bankResource.pendingContributionById(contributionId));
}
export function putPendingContributionById (request: PendingPersonalContributionPut) {
 return authorizedAxios.put<PendingPersonalContribution>(
   bankResource.pendingContributionById(request.pendingPersonalContributionId),
   request);
}
export function listTransactionsForEdit (args: TransactionEditSearch) {
  return authorizedAxios.get<TransactionEditSearchResult>(bankResource.transactionEditListing, args);
}
export function putTransactionEdit (request: TransactionEditPut) {
  return authorizedAxios.put<void>(bankResource.transactionEditTransaction(request.transactionId), request);
}
export function getDebitCards () {
  return authorizedAxios.get<DebitCardInfo[]>(bankResource.debitCards());
}
export function activateDebitCard (debitCardInfo: DebitCardInfo) {
  return authorizedAxios.put<boolean>(bankResource.activateDebitCard(), debitCardInfo);
}
export function deactivateDebitCard (debitCardInfo: DebitCardInfo) {
  return authorizedAxios.put<boolean>(bankResource.deactivateDebitCard(), debitCardInfo);
}
export function reissueDebitCard (debitCardInfo: DebitCardInfo) {
  return authorizedAxios.put<boolean>(bankResource.reissueDebitCard(), debitCardInfo);
}
export function getAvidiaStatements (year: number) {
  return authorizedAxios.get<AvidiaStatementsList>(bankResource.avidiaAccountStatements(year));
}
export function getHsaBankStatements (lookbackWindow: number) {
  return authorizedAxios.get<HsaBankStatementsList>(bankResource.hsaBankStatements(), {lookbackWindow});
}
export function getHsaBankCombinedStatements (lookbackWindow: number) {
  return authorizedAxios.get<HsaBankStatementsList>(bankResource.hsaBankCombinedStatements(), {lookbackWindow});
}
export function getHsaBankStatementKey (fundedAccountStatementIdentifier: string) {
  return authorizedAxios.post<AnonDocumentKey>(bankResource.hsaBankStatementKey(fundedAccountStatementIdentifier));
}
export function getHsaBankNotionalStatementKey (notionalAccountStatementIdentifier: string) {
  return authorizedAxios.post<AnonDocumentKey>(bankResource.hsaBankNotionalStatementKey(notionalAccountStatementIdentifier));
}
export function getHsaBankTaxDocuments (taxYear: number) {
  return authorizedAxios.get<HsaBankTaxDocumentList>(bankResource.hsaBankTaxDocuments(taxYear));
}
export function getHsaBankTaxDocumentKey (taxDocumentIdentifier: string) {
  return authorizedAxios.post<AnonDocumentKey>(
    bankResource.hsaBankTaxDocumentKey(taxDocumentIdentifier));
}

export function getAvidiaPartner (partnerId: number) {
  return authorizedAxios.get<AvidiaPartner>(bankResource.getAvidiaPartner(partnerId));
}
export function saveAvidiaPartner (avidiaPartner: AvidiaPartner) {
  return authorizedAxios.post<void>(bankResource.saveAvidiaPartner(), avidiaPartner);
}
export function getPriorYearContributionCutoffDate () {
  return authorizedAxios.get<PriorYearCutoffDateResponse>(bankResource.getPriorYearDate());
}

export function getPartnerCardCustomization (partnerId: number) {
  return authorizedAxios.get<DebitCardCustomization>(bankResource.partnerCardCustomization(partnerId));
}
export function setPartnerCardCustomization (request: PartnerCardCustomizationUpdateRequest) {
  return authorizedAxios.put<DebitCardCustomization>(bankResource.partnerCardCustomization(request.partnerId), request);
}
export function createCardCustomization (request: DebitCardCustomizationCreateRequest) {
  return authorizedAxios.post<DebitCardCustomization>(bankResource.cardCustomization(request.custodian), request);
}
export function updateCardCustomization (request: DebitCardCustomizationUpdateRequest) {
  return authorizedAxios.put<DebitCardCustomization>(bankResource.cardCustomization(request.custodian), request);
}
export function getCardCustomizations (custodian: BankCustodian) {
  return authorizedAxios.get<DebitCardCustomization[]>(bankResource.getCardCustomizations(custodian));
}
export function updateLinkedBankAccount (request: LinkedAccountManagementRequest) {
  return authorizedAxios.post<LinkedAccountManagementResponse>(bankResource.linkedAccountManagement(), request);
}
export function adminPendingTxSearch (request: AdminPendingTxSearchRequest) {
  return authorizedAxios.post<AdminPendingTxSearchResponse>(bankResource.pendingTxMgmt(), request);
}
export function adminPendingTxDelete (pendingTxId: number) {
  return authorizedAxios.delete<AdminPendingTxSearchResponse>(bankResource.pendingTxMgmt(pendingTxId));
}
export function getUmbAccountUser (emailAddress: string) {
  return authorizedAxios.get<UmbAccountUser>(bankResource.getUmbAccountUser(emailAddress));
}
export function reopenUmbAccount (request: UmbReopenAccountRequest) {
  return authorizedAxios.post(bankResource.reopenUmbAccount(), request);
}
export function getInstantTransferFailures (request: InstantTransferFailureRequest) {
  return authorizedAxios.get<InstantTransferPageResponseModel>(
    bankResource.getInstantTransferFailures(request.pageNumber, request.pageSize, request.isArchived));
}
export function getInstantTransferFailuresNew (request: InstantTransferFailureRequest) {
  return authorizedAxios.get<InstantTransferPageResponseModel>(
    bankResource.getInstantTransferFailuresNew(), request);
}
export function getInstantTransferFailureDetail (transactionId: string) {
  return authorizedAxios.get<InstantTransferFailureDetailModel>(bankResource.getInstantTransferFailureDetail(transactionId));
}
export function resolveInstantTransferFailure (transactionId: string) {
  return authorizedAxios.post<InstantTransferFailureDetailModel>(bankResource.resoloveInstantTransferFailure(transactionId));
}
export function retryInstantTransferFailure (transactionId: string) {
  return authorizedAxios.post<InstantTransferFailureDetailModel>(bankResource.retryInstantTransferFailure(transactionId));
}
export function getUmbAccountUsers (pageNumber: number, pageSize: number, searchTerm: string) {
  return authorizedAxios.get<UmbAccountUserList>(bankResource.getUmbAccountUsers(pageNumber, pageSize, searchTerm));
}
export function getUmbAccountUserById (umbAccountUserId: number) {
  return authorizedAxios.get<UmbAccountUserModel>(bankResource.getUmbAccountUserById(umbAccountUserId));
}
export function getHsabAccountTypes (args?: { username: string, partnerId: number}) {
  const queryStringParams = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
  return authorizedAxios.get<HsabAccountTypesResponse>(bankResource.getHsabAccountTypes(), queryStringParams);
}
export function getNotionalBalances (accountId: number) {
  return authorizedAxios.get<NotionalBalanceResult>(bankResource.getNotionalBalances(accountId));
}
export function getHsabAccountDisplayType () {
  return authorizedAxios.get<HsabAccountDisplayType>(bankResource.getHsabAccountDisplayType());
}
export function getHsabNotionalTransactions (accountId: number) {
  return authorizedAxios.get<HsabTransactionDetail[]>(bankResource.getHsabNotionalTransactions(accountId));
}
export function getHsabAccountDisplayTypeBatch (usernames: string[]) {
  return authorizedAxios.post<HsabAccountDisplayTypeBatch[]>(bankResource.getHsabAccountDisplayTypeBatch(), usernames);
}
export function verifyHsabAccount (request: HsabAccountVerifyRequest) {
  return authorizedAxios.post<HsabAccountVerifyResponse>(bankResource.verifyHsabAccount(), request);
}
export function UmbLinkedAccountSearch (request: AdminUmbLinkedAccountSearchRequest) {
  return authorizedAxios.post<AdminUmbLinkedAccountSearchResponse>(bankResource.umbLinkedAccountSearch(), request);
}
export function UmbLinkedAccountDelete (umbLinkedAccountId: number, hsaAccountNumber: string) {
  return authorizedAxios.delete<void>(bankResource.umbLinkedAccountDelete(umbLinkedAccountId, hsaAccountNumber));
}
export function getReimbursementFailures (request: ReimbursementFailurePageRequestModel) {
  return authorizedAxios.get<ReimbursementFailurePageResponseModel>(
    bankResource.getReimburementFailures(request.pageNumber, request.pageSize, request.isArchived));
}
export function getReimbursementFailureDetail (reimbursementId: number) {
  return authorizedAxios.get<ReimbursementFailureDetailModel>(bankResource.getReimbursementFailureDetail(reimbursementId));
}
export function resolveReimbursementFailure (reimbursementId: number) {
  return authorizedAxios.post<ReimbursementFailureDetailModel>(bankResource.resoloveReimbursementFailure(reimbursementId));
}
export function getBankUserAccountNumber (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<BankUserAccountNumber>(bankResource.bankUserAccountNumber(username), query);
}
export function postTransactionCodeFile (file: any) {
  return authorizedAxios.postFiles(bankResource.omnibusTransactionCodeUpload(), [file]);
}
export function getTransactionCodeResultFile (key: string) {
  return authorizedAxios.get<string>(bankResource.omnibusTransactionCodeResultFile(key));
}
export function getTransactionCodeExportFile () {
  return authorizedAxios.get<string>(bankResource.omnibusTransactionCodeExportFile());
}
export function getTransactionCodeResultFileExists (key: string) {
  return authorizedAxios.get<boolean>(bankResource.omnibusTransactionCodeResultFileExists(key));
}
export function getAccountHolds (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<BankAccountHoldsResponseModel>(bankResource.accountHolds(), query);
}
export function getHoldTypes () {
  return authorizedAxios.get<HoldTypeModel[]>(bankResource.holdTypes());
}
export function putHolds (partnerId: number, accountId: number, request: BankAccountHoldUpdateRequest) {
  return authorizedAxios.put<BankAccountHoldsModel>(bankResource.saveHolds(partnerId, accountId), request);
}
export function exportHoldHistory (accountId: number, username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<string>(bankResource.holdHistoryExport(accountId), query);
}
export function getAchCompanyName (partnerId: number) {
  return authorizedAxios.get<PartnerAchBrandingResponse>(bankResource.achCompanyName(partnerId));
}
export function putAchCompanyName (partnerId: number, request: PartnerAchBrandingRequest) {
  return authorizedAxios.put<string>(bankResource.achCompanyName(partnerId), request);
}
export function getDistributionSchedules (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<TransferSchedule[]>(bankResource.distributionSchedule(), query);
}
export function getPersonalContributionSchedule (scheduleId: number) {
  return authorizedAxios.get<TransferSchedule>(bankResource.getpersonalContributionSchedule(scheduleId));
}
export function getPersonalContributionSchedules (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<TransferSchedule[]>(bankResource.personalContributionSchedule(), query);
}
export function addPersonalContributionSchedule (request: TransferSchedule) {
  return authorizedAxios.post<void>(bankResource.personalContributionSchedule(), request);
}
export function updatePersonalContributionSchedule (request: TransferSchedule) {
  return authorizedAxios.put<void>(
    bankResource.personalContributionScheduleUpdate(request.scheduleId), request);
}
export function cancelPersonalContributionSchedule (scheduleId: number, username: string, partnerId: number) {
  const userQuery = (username) ? { forUser: username, forUserPartnerId: partnerId } : {};
  return authorizedAxios.request<void>({
    url: bankResource.cancelPersonalContributionSchedule(scheduleId),
    method: "PUT",
    params: userQuery
  });
}
export function getFutureContributions (username: string, partnerId: number, startDate: string, endDate: string) {
  const userQuery = (username) ? { forUser: username, forUserPartnerId: partnerId } : {};
  const query = { startDate, endDate, ...userQuery};
  return authorizedAxios.get<FutureContribution[]>(
    bankResource.futureContributions(), query);
}
export function getProposedPersonalContributionScheduleCalendars (
  proposedSchedule: TransferSchedule,
  numberOfCalendarEntries: number) {
    return authorizedAxios.post<FutureContribution[]>(
      bankResource.proposedPersonalContributionScheduleCalendars(numberOfCalendarEntries), proposedSchedule);
}
export function getPersonalContributionSchedulingEligibility () {
  return authorizedAxios.get<boolean>(bankResource.personalContributionSchedulingEligibility());
}
export function getPersonalProjectedContributionsForYear (selectedYear: number) {
  const query = { selectedYear };
  return authorizedAxios.get<number>(bankResource.getPersonalProjectedContributionsForYear(), query);
}
export function getProposedPersonalContributionScheduleCalendarsToDate (
  proposedSchedule: TransferSchedule,
  toDate: string) {
    return authorizedAxios.post<FutureContribution[]>(
      bankResource.proposedPersonalContributionScheduleCalendarsByDate(toDate), proposedSchedule);
}
export function getHsabHsaDailyBalances (startDate: string,
                                         endDate: string,
                                         username: string,
                                         partnerId: number) {
  const data = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<BankTransactionDailyBalancePage>(
      bankResource.getHsabDailyBalances(startDate, endDate), data);

}

export function getAuthorizedSigners (args?: { username: string, partnerId: number }) {
  const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
  return authorizedAxios.get<GetAuthorizedSignerResponse>(bankResource.authorizedSigners(), params);
}

export function addAuthorizedSigner (request: AddAuthorizedSignerRequest) {
  return authorizedAxios.post<AddAuthorizedSignerResponse>(bankResource.authorizedSigners(), request);
}

export function updateAuthorizedSigner (id: number, authorizedSigner: AuthorizedSigner) {
  return authorizedAxios.put<UpdateAuthorizedSignerResponse>(bankResource.updateAuthorizedSigner(id), authorizedSigner);
}
export function getDebitCardEligibilityForAuthorizedSigner () {
  return authorizedAxios.get<DebitCardEligibilityResponse>(bankResource.getDebitCardEligibityForAuthorizedSigner());
}

export function getBeneficiaries (args?: { username: string, partnerId: number} ) {
  const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId} : undefined;
  return authorizedAxios.get<GetBeneficiaryResponse>(bankResource.beneficiaries(), params);
}

export function addBeneficiary (request: BeneficiaryUpdateRequest) {
  return authorizedAxios.post<AddBeneficiaryResponse>(bankResource.beneficiaries(), request);
}

export function postCpfBlackout (date: string) {
  return authorizedAxios.post(bankResource.cpfBlackout(), { date });
}

export function getCpfBlackoutDates () {
  return authorizedAxios.get<string[]>(bankResource.cpfBlackout());
}

export function deleteCpfBlackoutDate (date: string) {
  return authorizedAxios.delete(bankResource.cpfBlackoutDelete(date));
}

export function updateBeneficiary (beneficiary: BeneficiaryUpdateRequest) {
  return authorizedAxios.put<BeneficiaryUpdateResponse>(bankResource.beneficiaries(), beneficiary);
}

export function GetPlanYearEnrollmentDetails () {
  return authorizedAxios.get<PlanYearEnrollmentDetailsResponse>(bankResource.getPlanYearEnrollmentDetails());
}

export function SetPlanYearEnrollmentReimbursementMethod (request: HsabEnrollmentUpdateReimbursementMethodRequest) {
  return authorizedAxios.put(bankResource.setPlanYearEnrollmentReimbursementMethod(), request);
}

export function updateHsabEnrollmentSettings (settings: HsabEnrollmentSettingsUpdateRequest) {
  return authorizedAxios.put(bankResource.enrollmentSettingsUpdate(), settings);
}

export function getUserHsaAccountsBySsn (ssn: string) {
  return authorizedAxios.post<HsaAccountDetails[]>(bankResource.userHsaAccountsSearchBySsn(), {ssn});
}

export function saveRepointInvestmentAccount (request: InvestmentAccountRepointRequest) {
  return authorizedAxios.post<boolean>(bankResource.saveAccountRepoint(), request);
}

export function getInvestmentRepointRequests (pageNumber: number, pageSize: number) {
  return authorizedAxios.get<InvestmentRepointRequestResult>(bankResource.getInvestmentRepointRequests(pageNumber, pageSize));
}

export function getUserHsaCustodianByPartner (partnerId: number) {
  return authorizedAxios.get<UserCustodianSettings>(bankResource.getUserHsaCustodianByPartner(), {partnerId});
}

export function getAllContributionActivity (username: string, partnerId: number,
  args: { page: number, contributionType?: ContributionType }) {
  const query = (username) ? {
    page: args.page,
    contributionType: args.contributionType,
    forUser: username,
    forUserPartnerId: partnerId
  } : {
    page: args.page,
    contributionType: args.contributionType
  };

  return authorizedAxios.get<ContributionActivityList>(bankResource.getAllContributionActivity(), query);
}